import { cn } from "@/lib/utils";

interface SwitchProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  onCheckedChange?: (checked: boolean) => void;
}

function Switch({ className, label, description, onCheckedChange, checked, ...props }: SwitchProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheckedChange) {
      onCheckedChange(e.target.checked);
    }
  };

  return (
    <label
      htmlFor={props.id}
      className={cn("flex w-fit cursor-pointer items-center gap-3", description && "items-start", className)}
    >
      <div
        className={cn(
          "focus-within:border-ring focus-within:ring-blue-brand/50 relative inline-flex h-5.5 w-9 shrink-0 items-center rounded-full border border-transparent p-0.5 transition-all outline-none focus-within:ring-[3px] focus-within:ring-offset-2"
        )}
      >
        <input
          type="checkbox"
          onChange={handleChange}
          checked={checked}
          className="peer absolute z-10 h-full w-full cursor-pointer opacity-0"
          {...props}
        />
        <span
          className={cn(
            "absolute inset-0 rounded-full transition-colors",
            "bg-input",
            "peer-checked:bg-blue-brand",
            "peer-disabled:opacity-50",
            "peer-disabled:cursor-not-allowed"
          )}
        />
        <span
          data-slot="switch-thumb"
          className={cn(
            "bg-background pointer-events-none block size-[0.9rem] translate-x-0 rounded-full ring-0 transition-transform ease-in-out",
            "peer-checked:translate-x-[calc(100%)]"
          )}
        />
      </div>
      {(label || description) && (
        <div className="leading-h1 flex flex-col items-start gap-0.5 text-base font-medium">
          {label}
          {description && <p className="leading-body-small text-sm font-normal text-slate-500">{description}</p>}
        </div>
      )}
    </label>
  );
}

export { Switch };
